import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

/**
 * Custom hook for managing filters, pagination, and sorting in URL
 * Automatically syncs state with URL search params
 * Stores page_size preference per route in localStorage
 *
 * @param {Object} defaultFilters - Default filter values
 * @param {string} storageKey - Optional custom key for localStorage (defaults to current route path)
 * @returns {Object} { filters, setFilters, updateFilters }
 *
 * @example
 * const { filters, updateFilters } = useUrlFilters({
 *   search: '',
 *   page: 1,
 *   page_size: 20,
 * })
 */
export function useUrlFilters(defaultFilters = {}, storageKey = null) {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Use provided storageKey or derive from current route path
  const localStorageKey = storageKey || `pageSize_${location.pathname.replace(/\//g, '_')}`

  // Get saved page_size from localStorage
  const getSavedPageSize = () => {
    try {
      const saved = localStorage.getItem(localStorageKey)
      return saved ? parseInt(saved, 10) : defaultFilters.page_size
    } catch (error) {
      console.error('Failed to read page_size from localStorage:', error)
      return defaultFilters.page_size
    }
  }

  // Initialize with saved page_size preference
  const initialFilters = {
    ...defaultFilters,
    page_size: getSavedPageSize()
  }

  const [filters, setFilters] = useState(initialFilters)
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize filters from URL on mount
  useEffect(() => {
    const urlFilters = {
      ...defaultFilters,
      page_size: getSavedPageSize() // Use saved page_size as default
    }
    let hasUrlParams = false

    // Parse all URL parameters
    for (const [key, value] of searchParams.entries()) {
      hasUrlParams = true

      // Handle numeric values (page, page_size, etc.)
      if (key === 'page' || key === 'page_size') {
        urlFilters[key] = parseInt(value, 10) || defaultFilters[key]
      } else {
        urlFilters[key] = value
      }
    }

    // If page_size is in URL, save it to localStorage
    if (urlFilters.page_size && urlFilters.page_size !== defaultFilters.page_size) {
      try {
        localStorage.setItem(localStorageKey, String(urlFilters.page_size))
      } catch (error) {
        console.error('Failed to save page_size to localStorage:', error)
      }
    }

    // Only update state if URL has params or if first initialization
    if (hasUrlParams || !isInitialized) {
      setFilters(urlFilters)
      setIsInitialized(true)
    }
  }, []) // Only run on mount

  /**
   * Update filters and sync to URL
   * Automatically resets page to 1 when filters change (except pagination changes)
   * Saves page_size to localStorage when changed
   */
  const updateFilters = (updates, resetPage = true) => {
    const newFilters = {
      ...filters,
      ...updates,
      // Reset to page 1 when filters change (unless explicitly updating page)
      ...(resetPage && !updates.hasOwnProperty('page') && !updates.hasOwnProperty('pageIndex') ? { page: 1 } : {})
    }

    // Save page_size to localStorage if it changed
    if (updates.page_size && updates.page_size !== filters.page_size) {
      try {
        localStorage.setItem(localStorageKey, String(updates.page_size))
      } catch (error) {
        console.error('Failed to save page_size to localStorage:', error)
      }
    }

    setFilters(newFilters)
    syncToUrl(newFilters)
  }

  /**
   * Sync filters to URL search params
   * Only includes non-empty values to keep URL clean
   */
  const syncToUrl = (filtersToSync) => {
    const params = new URLSearchParams()

    Object.entries(filtersToSync).forEach(([key, value]) => {
      // Skip empty values and default page values to keep URL clean
      if (value !== '' && value !== null && value !== undefined) {
        // Don't add page=1 to URL (it's the default)
        if (key === 'page' && value === 1) return
        // Don't add page_size to URL if it matches saved preference (it's stored in localStorage)
        if (key === 'page_size' && value === getSavedPageSize()) return

        params.set(key, String(value))
      }
    })

    setSearchParams(params, { replace: true })
  }

  /**
   * Handle pagination changes from Table component
   * Compatible with TanStack Table's pagination updater pattern
   */
  const handlePaginationChange = (updater) => {
    const currentPagination = {
      pageIndex: filters.page - 1,
      pageSize: filters.page_size
    }

    const newPagination = typeof updater === 'function'
      ? updater(currentPagination)
      : updater

    updateFilters({
      page: newPagination.pageIndex + 1,
      page_size: newPagination.pageSize
    }, false) // Don't reset page when explicitly changing pagination
  }

  /**
   * Handle sorting changes from Table component
   * Converts TanStack Table sorting format to Django ordering format
   * @param {Array|Function} updater - Sorting state or updater function
   *
   * TanStack format: [{id: 'name', desc: false}]
   * Django format: 'name' or '-name' (minus prefix for descending)
   */
  const handleSortingChange = (updater) => {
    // Get current sorting state from filters.ordering
    const currentSorting = filters.ordering ? [
      {
        id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
        desc: filters.ordering.startsWith('-')
      }
    ] : []

    // Apply updater if it's a function
    const newSorting = typeof updater === 'function'
      ? updater(currentSorting)
      : updater

    // Convert to Django ordering format
    let ordering = ''
    if (newSorting && newSorting.length > 0) {
      const sort = newSorting[0] // Django typically supports single column sorting
      ordering = sort.desc ? `-${sort.id}` : sort.id
    }

    updateFilters({ ordering })
  }

  /**
   * Reset all filters to default values
   */
  const resetFilters = () => {
    setFilters(defaultFilters)
    setSearchParams({}, { replace: true })
  }

  return {
    filters,
    setFilters,
    updateFilters,
    handlePaginationChange,
    handleSortingChange,
    resetFilters,
    isInitialized
  }
}
